import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Home,
  Briefcase,
  Warehouse,
  MousePointerClick,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de fournitures de déménagement à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que votre temps est précieux et que transporter des
              piles de cartons plats dans un véhicule personnel est une
              corvée, notre service logistique de proximité gère tout
              pour vous.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture locale totale",
                body: "Nous livrons l'intégralité de vos commandes directement sur le pas de votre porte au Mesnil-le-Roi, ainsi que dans les communes voisines comme Saint-Germain-en-Laye, Maisons-Laffitte, Sartrouville et Le Pecq.",
                Icon: MapPin,
              },
              {
                title: "Un timing respecté",
                body: "Finie l'attente interminable ; nous convenons ensemble d'un créneau horaire précis pour s'adapter parfaitement à votre emploi du temps et vous permettre de cartonner sans transition.",
                Icon: Clock,
              },
              {
                title: "Un matériel impeccable",
                body: "Toutes nos fournitures vous sont remises au sec, parfaitement propres et prêtes à l'emploi pour accueillir immédiatement vos vêtements, vaisselles et souvenirs.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Accompagnement projets ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Tous vos projets
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Un accompagnement pour tous vos projets de déménagement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque projet de vie est unique et demande des outils
            adaptés au volume ainsi qu&apos;à la nature des biens à
            transférer. Notre catalogue est conçu pour répondre
            précisément à toutes les configurations.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Pour les déménagements familiaux",
              body: "Nous mettons à votre disposition des kits tout-en-un, des cartons penderies verticaux pour vos dressings et des cartons à croisillons spécifiques pour sécuriser votre vaisselle fine et vos verres sans risque de casse.",
              Icon: Home,
            },
            {
              title: "Pour les transferts d'entreprises et bureaux",
              body: "Simplifiez le déménagement de votre activité professionnelle grâce à nos cartons d'archives ultra-robustes à poignées renforcées et nos bacs de manutention empilables pour le matériel informatique.",
              Icon: Briefcase,
            },
            {
              title: "Pour le stockage à long terme en garde-meuble",
              body: "Protégez durablement vos affaires contre la poussière et les variations d'humidité grâce à nos cartons double cannelure rigides, nos films étirables opaques et nos housses de protection pour matelas.",
              Icon: Warehouse,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-le-mesnil-le-roi-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits par typologie
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-le-mesnil-le-roi-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; protections
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Commandez en quelques clics ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Commande en ligne
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Commandez vos cartons en quelques clics
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Gagnez du temps et simplifiez vos préparatifs grâce à
              notre plateforme en ligne ultra-intuitive. En seulement
              trois étapes, votre commande est validée et planifiée.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Vous sélectionnez vos cartons à l&apos;unité ou en kit
              selon votre volume, vous choisissez votre créneau de
              livraison à domicile {city.aCity}, et vous réglez en
              toute sécurité. Plus besoin de courir les magasins ni de
              reporter vos cartons : votre matériel professionnel arrive
              directement chez vous, prêt à l&apos;emploi, pour vous
              permettre de démarrer vos cartons au moment où vous le
              décidez.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-le-mesnil-le-roi-cta-shop"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Voir toute la gamme
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/livraison"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-le-mesnil-le-roi-cta-livraison"
              >
                <Truck className="h-4 w-4" strokeWidth={2.6} />
                Nos modalités de livraison
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 space-y-5">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <MousePointerClick className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  3 étapes seulement
                </h3>
              </div>
              {[
                {
                  step: "1",
                  title: "Sélectionnez vos cartons",
                  body: "À l'unité ou en kit selon votre volume (studio, T2, maison familiale, transfert pro).",
                },
                {
                  step: "2",
                  title: "Choisissez votre créneau",
                  body: `Réservez une livraison à domicile ${city.aCity} sur la journée et le créneau qui vous arrangent.`,
                },
                {
                  step: "3",
                  title: "Réglez en toute sécurité",
                  body: "Paiement sécurisé en ligne ou à la livraison : votre commande est immédiatement planifiée.",
                },
              ].map(({ step, title, body }) => (
                <div key={step} className="flex items-start gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full bg-white border border-[#237cb5] text-[#237cb5] font-bold text-[13px]">
                    {step}
                  </span>
                  <div>
                    <div className="font-display font-extrabold text-[14.5px] text-[#0f1d2b]">
                      {title}
                    </div>
                    <p className="mt-1 text-[13.5px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-le-mesnil-le-roi-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
